/**
 * This is a temporary fix related to flag `doc-upload-redesign-use-legacy-message-keys`
 */

import { SegmentEnum, SubSegmentEnum } from "../../lib/types/runtimeTypes";
import { type NestedMessagePaths, type Segment, type SubSegment } from "../../lib/types/types";

const LegacySegmentUploadInfo: Record<SegmentEnum, NestedMessagePaths[]> = {
  [SegmentEnum.STUDENT]: [
    "step.docUpload.uploadInfo.fullName",
    "step.docUpload.uploadInfo.student.school",
    "step.docUpload.uploadInfo.student.enrollmentDate",
  ],
  [SegmentEnum.TEACHER]: [
    "step.docUpload.uploadInfo.fullName",
    "step.docUpload.uploadInfo.teacher.school",
    "step.docUpload.uploadInfo.teacher.currentSchoolYear",
  ],
  [SegmentEnum.MEMBER]: [
    "step.docUpload.uploadInfo.fullName",
    "step.docUpload.uploadInfo.organization",
    "step.docUpload.uploadInfo.affiliation",
  ],
  [SegmentEnum.MILITARY]: [
    "step.docUpload.uploadInfo.fullName",
    "step.docUpload.uploadInfo.military.serviceBranch",
    "step.docUpload.uploadInfo.military.dischargeDate",
    "step.docUpload.uploadInfo.military.currentAffiliation",
  ],
  [SegmentEnum.SENIOR]: [
    "step.docUpload.uploadInfo.fullName",
    "step.docUpload.uploadInfo.senior.birthDate",
  ],
  [SegmentEnum.AGE]: [
    "step.docUpload.uploadInfo.fullName",
    "step.docUpload.uploadInfo.age.birthDate",
  ],
  [SegmentEnum.FIRST_RESPONDER]: [
    "step.docUpload.uploadInfo.fullName",
    "step.docUpload.uploadInfo.firstResponder.organization",
    "step.docUpload.uploadInfo.firstResponder.currentAffiliation",
    "step.docUpload.uploadInfo.firstResponder.license",
  ],
  [SegmentEnum.MEDICAL]: [
    "step.docUpload.uploadInfo.fullName",
    "step.docUpload.uploadInfo.medical.status",
    "step.docUpload.uploadInfo.medical.validDate",
  ],
  [SegmentEnum.EMPLOYMENT]: [
    "step.docUpload.uploadInfo.employment.company",
    "step.docUpload.uploadInfo.employment.currentAffiliation",
  ],
  [SegmentEnum.IDENTITY]: [
    "step.docUpload.uploadInfo.fullName",
    "step.docUpload.uploadInfo.identity.validDate",
  ],
  [SegmentEnum.LICENSED_PROFESSIONAL]: [
    "step.docUpload.uploadInfo.fullName",
    "step.docUpload.uploadInfo.licensedProfessional.licenseStatus",
    "step.docUpload.uploadInfo.identity.validDate",
  ],
  [SegmentEnum.LOW_INCOME]: [
    "step.docUpload.uploadInfo.fullName",
    "step.docUpload.uploadInfo.lowIncome.validDate",
  ],
  [SegmentEnum.RECENT_MOVER]: [
    "step.docUpload.uploadInfo.fullName",
    "step.docUpload.uploadInfo.recentMover.address",
    "step.docUpload.uploadInfo.recentMover.validDate",
  ],
};

const LegacySubSegmentUploadInfo: Record<SubSegmentEnum, NestedMessagePaths[]> = {
  [SubSegmentEnum.ACTIVE_DUTY]: [],
  [SubSegmentEnum.VETERAN]: [],
  [SubSegmentEnum.RESERVIST]: [],
  [SubSegmentEnum.MILITARY_RETIREE]: [],
  [SubSegmentEnum.MILITARY_FAMILY]: [
    // Overridden in SubSegmentUploadInfoOverrides
  ],
  [SubSegmentEnum.GOLD_STAR_FAMILY]: [],
  [SubSegmentEnum.FIREFIGHTER]: [],
  [SubSegmentEnum.POLICE]: [],
  [SubSegmentEnum.EMT]: [],
  [SubSegmentEnum.NURSE]: [],
  [SubSegmentEnum.DRIVER_LICENSE]: [],
  [SubSegmentEnum.GENERAL_IDENTITY]: [],
  [SubSegmentEnum.HYBRID_IDENTITY]: [],
  [SubSegmentEnum.HOME_BUYER]: [],
  [SubSegmentEnum.OTHER_MOVER]: [],
  [SubSegmentEnum.LICENSED_COSMETOLOGIST]: [],
  [SubSegmentEnum.LICENSED_REAL_ESTATE_AGENT]: [],
  [SubSegmentEnum.VETERINARIAN]: [],
  [SubSegmentEnum.SNAP_BENEFITS]: ["step.docUpload.uploadInfo.lowIncome.ebtNumber"],
  [SubSegmentEnum.OTHER_GOVERNMENT_ASSISTANCE]: [],
  [SubSegmentEnum.CHILD_CARE_WORKER]: [],
  [SubSegmentEnum.LIBRARIAN]: [],
  [SubSegmentEnum.INTERIOR_DESIGNER]: [],
  [SubSegmentEnum.ARCHITECT]: [],
  [SubSegmentEnum.GENERAL_CONTRACTOR]: [],
  [SubSegmentEnum.NUTRITION_PROFESSIONAL]: [],
};

// Subsegment messages are usually added to the segment messages, but some
// should replace the main segment messages, they go here.
const LegacySubSegmentUploadInfoOverrides: Partial<Record<SubSegmentEnum, NestedMessagePaths[]>> = {
  // MILITARY_FAMILY is overridden to not show branch of service.
  [SubSegmentEnum.MILITARY_FAMILY]: [
    "step.docUpload.uploadInfo.military.uploadInstructionsDependent",
  ],
};

export function getLegacyUploadInfo(
  segment: Segment,
  subSegment: SubSegment,
): NestedMessagePaths[] {
  return (
    LegacySubSegmentUploadInfoOverrides[subSegment] || [
      ...(LegacySubSegmentUploadInfo[subSegment] || []),
      ...(LegacySegmentUploadInfo[segment] || []),
    ]
  );
}
